import { z } from 'zod';
import { isValidIccid, isValidImei, isValidLicense, normalizeUsPhone } from '../lib/identifiers.js';
export const uuidSchema = z.uuid();
export const imeiSchema = z
    .string()
    .trim()
    .refine(isValidImei, { message: 'Must be a valid 15-digit IMEI (checksum failed)' });
export const iccidSchema = z
    .string()
    .trim()
    .refine(isValidIccid, { message: 'Must be a valid 19-20 digit ICCID (checksum failed)' });
/** Accepts any identifier kind — for a boundary shared by phones, SIMs and licenses,
 *  where the exact kind expected is checked against the product afterward. */
export const unitIdentifierSchema = z
    .string()
    .trim()
    .refine((v) => isValidImei(v) || isValidIccid(v) || isValidLicense(v), {
    message: 'Must be a valid IMEI, ICCID or license number',
});
/** Shape-only (IMEI/ICCID length, or a license number's characters) — for endpoints that look an
 *  existing unit up by its identifier rather than record a new one. No Luhn
 *  check: a SIM received with a checksum override (t0918) must still be
 *  findable by scan; whether the unit exists is the real check. */
export const unitIdentifierLookupSchema = z
    .string()
    .trim()
    .regex(/^(\d{15}|\d{19,20}|[A-Za-z0-9][A-Za-z0-9._-]{3,63})$/, 'Must be a 15-digit IMEI, a 19-20 digit ICCID or a license number');
/** Accepts loose human input, always produces E.164. */
export const usPhoneSchema = z
    .string()
    .trim()
    .transform((v, ctx) => {
    const normalized = normalizeUsPhone(v);
    if (!normalized) {
        ctx.addIssue({ code: 'custom', message: 'Must be a valid US phone number' });
        return z.NEVER;
    }
    return normalized;
});
/** Already-normalized number, e.g. coming back out of the database. */
export const e164Schema = z.string().regex(/^\+1\d{10}$/, 'Must be an E.164 US number');
export const moneyCentsSchema = z
    .number()
    .int('Amounts are stored in whole cents')
    .nonnegative()
    .max(100_000_00, 'Amount exceeds the maximum allowed');
export const emailSchema = z.email().max(254).toLowerCase();
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    sort: z.string().max(64).optional(),
    order: z.enum(['asc', 'desc']).default('desc'),
    q: z.string().trim().max(200).optional(),
});
// ------------------------------------------------------------- bulk import
/**
 * One CSV row, already header-mapped by the admin dialog into field names the
 * server understands. Deliberately loose — a real row schema would 400 the
 * whole batch on one bad cell, and the whole point of a bulk import is that
 * one bad row gets reported, not that it takes the other 499 down with it.
 */
export const importRowSchema = z.record(z.string().max(64), z.union([z.string().max(4000), z.number(), z.boolean(), z.null()]));
export const importRowSuccessSchema = z.object({
    /** Position in the submitted `rows` array, so the dialog can point back at
     *  the exact CSV line it came from. */
    index: z.number().int().nonnegative(),
    id: uuidSchema,
    /** The value the row was matched or created by (SKU, email, phone…). */
    key: z.string(),
});
export const importRowFailureSchema = z.object({
    index: z.number().int().nonnegative(),
    key: z.string().nullable(),
    reason: z.string(),
});
export function importResultSchema() {
    return z.object({
        created: z.array(importRowSuccessSchema),
        updated: z.array(importRowSuccessSchema),
        failed: z.array(importRowFailureSchema),
    });
}
export function paginatedSchema(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().nonnegative(),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
    });
}
/** Uniform error envelope returned by every API route. */
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
/**
 * The body shape for a PATCH: every field optional, and nothing invented for
 * the fields the caller left out.
 *
 * `.partial()` on its own is not enough. A field carrying `.default(...)` —
 * which most create schemas use, so a new record can omit what it has no
 * value for — still parses to that default when the key is absent, so the
 * route spreads it into the UPDATE and writes it over what is stored. That
 * is how switching a product to inactive erased its category, brand,
 * description and reorder point: the toggle sent `{ isActive: false }` and
 * everything else arrived as its default. Dropping the defaults first means
 * an absent key stays absent all the way to the database.
 */
export function patchableSchema(schema) {
    const shape = Object.fromEntries(Object.entries(schema.shape).map(([key, field]) => [
        key,
        field instanceof z.ZodDefault ? field.def.innerType : field,
    ]));
    return z.object(shape).partial();
}
//# sourceMappingURL=common.js.map